/*
 * Copyright (c) 2023 Irian Software Development SRL. All Rights Reserved.
 * This software is the proprietary information of Irian Software Development SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Injectable } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { Location } from "@angular/common";
import { BehaviorSubject, filter, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { reject } from "lodash";

@Injectable({
  providedIn: 'root',
})
export class BackNavigationService {
  private history: string[] = [];

  private latestRouterState: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );

  constructor(
    private readonly router: Router,
    private readonly location: Location
  ) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.history.push(event.urlAfterRedirects);
        this.latestRouterState.next(
          this.router.getCurrentNavigation()?.extras?.state
        );
      }
    });
  }

  closeSidebar<T>(data?: T) {
    if (data !== undefined && data !== null) {
      this.router.navigate([{outlets: {sidebar: null}}], {state: data})
    } else {
      this.router.navigate([{outlets: {sidebar: null}}])
    }
  }

  closeFullPopup<T>(data?: T) {
    if (data !== undefined && data !== null) {
      this.router.navigate([{outlets: {'popup-full': null}}], {state: data})
    } else {
      this.router.navigate([{outlets: {'popup-full': null}}])
    }
  }

  closePopup<T>(data?: T) {
    if (data !== undefined && data !== null) {
      this.router.navigate([{outlets: {'popup': null}}], {state: data})
    } else {
      this.router.navigate([{outlets: {'popup': null}}])
    }
  }

  back(data?: any, preserveRouterOutletHistoryOrder = false): void {
    const isRouterOutlet: boolean =
      this.history.length >= 1
        ? this.history[this.history.length - 1].search(/\s*\(.*?\)\s*/g) != -1
        : false;
    if (this.history.length <= 1 && !isRouterOutlet) {
      this.location.back();
    } else {
      const lastUrl =
        this.history[
        this.history.length -
        (isRouterOutlet && !preserveRouterOutletHistoryOrder ? 1 : 2)
          ];
      let navigationUrl = lastUrl;
      if (!preserveRouterOutletHistoryOrder) {
        const filtered = lastUrl.replace(/\s*\(.*?\)\s*/g, '');
        navigationUrl = lastUrl.search(/\s*\(.*?\)\s*/g) ? filtered : '/';
      }

      if (data) {
        this.router.navigateByUrl(navigationUrl, {state: data});
      } else {
        this.router.navigateByUrl(navigationUrl);
      }
    }
  }

  backExcludingDuplicateHistory(data?: any) {
    const historyExcludingRouterOutletsUrls = this.history.filter(
      (historyUrl) => historyUrl.search(/\s*\(.*?\)\s*/g) == -1
    );
    const historyExcludingConsecutiveDuplicates = reject(
      historyExcludingRouterOutletsUrls,
      (url, index) => {
        return (
          index > 0 && historyExcludingRouterOutletsUrls[index - 1] === url
        );
      }
    );
    if (historyExcludingConsecutiveDuplicates.length >= 2) {
      const backUrl =
        historyExcludingConsecutiveDuplicates[
        historyExcludingConsecutiveDuplicates.length - 2
          ];
      if (data) {
        this.router.navigateByUrl(backUrl, {state: data});
      } else {
        this.router.navigateByUrl(backUrl);
      }
    } else {
      this.location.back();
    }
  }

  getNavigationData(): Observable<{ [k: string]: any }> {
    return this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.router.getCurrentNavigation()?.extras?.state),
      filter((res) => res != null),
    );
  }

  getLatestRouterState(): Observable<any> {
    return this.latestRouterState.asObservable();
  }
}
